// @ts-strict-ignore
import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { BaseService } from '../base';
import { GenericAPIResponse, Project } from '../../definitions';

@Injectable()
export class FunderAllocationService extends BaseService {
  private getFunderUrl = `${this.baseUrl}/tax/funder-allocation`;
  private saveSwapUrl = `${this.baseUrl}/tax/funder-allocation/swap/allocate`;
  private calculateSwapUrl = `${this.baseUrl}/tax/funder-allocation/swap/calculate`;
  private savePurchaseUrl = `${this.baseUrl}/tax/funder-allocation/purchase/allocate`;
  private calculatePurchaseUrl = `${this.baseUrl}/tax/funder-allocation/purchase/calculate`;
  private exportPurchaseUrl = `${this.baseUrl}/tax/funder-allocation/purchase/export`;

  constructor(private http: HttpClient, @Inject('project') public project: Project) {
    super(project);
  }

  // Get a nice list of funders available for swap allocation
  public getFunders(): Observable<Funder[]> {
    return this.http.get<GenericAPIResponse<Funder[]>>(this.getFunderUrl).pipe(
      map((response: GenericAPIResponse<Funder[]>) => {
        return response.data.map((funder: Funder) => {
          return {
            ...funder,
            selected: false,
          };
        });
      }),
    );
  }

  // Run the funder swap allocation calculation
  public calculateSwap(
    funderAccountIds: number[],
    runItemIds: number[],
  ): Observable<FunderAllocation[]> {
    return this.http
      .post<GenericAPIResponse<any>>(this.calculateSwapUrl, {
        funderAccountIds,
        runItemIds,
      })
      .pipe(
        map((response: GenericAPIResponse<FunderAllocation[]>) => {
          return response.data.map((funder: FunderAllocation) => {
            return {
              ...funder,
              swapFromDepositDate: new Date(funder.swapFromDepositDate),
              swapToDepositDate: new Date(funder.swapToDepositDate),
            };
          });
        }),
      );
  }

  // Save the previously run swap allocation
  public saveSwap(): Observable<GenericAPIResponse<void>> {
    return this.http.post<GenericAPIResponse<void>>(this.saveSwapUrl, {});
  }

  // Run the funder swap allocation calculation
  public calculatePurchases(
    funderAccountId: number,
    runItemIds: number[],
  ): Observable<GenericAPIResponse<PurchaseAllocationMode>> {
    return this.http.post<GenericAPIResponse<PurchaseAllocationMode>>(this.calculatePurchaseUrl, {
      funderAccountId,
      runItemIds,
    });
  }

  // Save the previously run swap allocation
  public savePurchases(): Observable<GenericAPIResponse<void>> {
    return this.http.post<GenericAPIResponse<void>>(this.savePurchaseUrl, {});
  }

  public exportProposedPurchases(): Observable<string> {
    return this.http.get(this.exportPurchaseUrl, {
      responseType: 'text',
    });
  }
}

export type Funder = {
  accountId: number;
  name: string;
  hasOwner: boolean;
  selected: boolean;
};

export type FunderAllocation = {
  funderName: string;
  amount: number;
  swapFromTaxYear: number;
  swapToTaxYear: number;
  swapFromDepositDate: Date;
  swapToDepositDate: Date;
};

export type PurchaseAllocationMode = {
  purchaseAllocationMode: 'TAXPAYERSONLY' | 'FUNDERS';
};
